import { tellShellTheme } from '../app/desktop';

/**
 * Which palette the interface is drawn in.
 *
 * ## Three states, not two
 *
 * `system` is the default and is not the same as either fixed choice: it means
 * "follow the device", and the device can change under the app - a phone
 * switching at sunset, a laptop on a schedule. So `system` stores nothing and
 * removes the attribute, leaving the `prefers-color-scheme` block in the
 * stylesheet to decide. A boolean would have to guess an initial value and would
 * then be wrong for everybody whose device disagreed with the guess.
 *
 * ## The attribute is the whole mechanism
 *
 * Setting `data-theme` on `<html>` is all this does. Every colour in the app
 * resolves through `--chrome-*`, and the stylesheet restates that block under
 * `:root[data-theme='light']`. Nothing re-renders, no context is needed, and a
 * component cannot get it wrong because no component is involved.
 *
 * ## Applied before first paint
 *
 * By a small inline script in `index.html`, not from here. A module runs after
 * the document has been painted at least once, which is long enough to show
 * somebody a dark screen before their light one arrives.
 *
 * That script is deliberately tiny and silent: it reads `THEME_KEY`, sets the
 * attribute, and swallows any storage error - private browsing or blocked site
 * data leaves `prefers-color-scheme` to decide, which is the same answer this
 * module gives. It spells the key literally, because it runs before any module
 * exists to import it from. **Change `THEME_KEY` here and you must change it
 * there**; nothing enforces it, and the failure is silent - a stored preference
 * that stops being read, so every load flashes the wrong theme.
 *
 * (That reasoning used to live in a comment beside the script. `index.html` is
 * served to every visitor, so its comments were stripped; the knowledge moved
 * here rather than being deleted with them.)
 */
export type Theme = 'system' | 'light' | 'dark';

/** Shared with the inline script in `index.html`. Change both or neither. */
export const THEME_KEY = 'gv:theme';

export function getTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);

    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    /*
     * Private browsing, or storage disabled entirely. Following the device is
     * the honest answer when the preference cannot be read.
     */
    return 'system';
  }
}

export function setTheme(theme: Theme): void {
  const root = document.documentElement;

  if (theme === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }

  try {
    if (theme === 'system') {
      localStorage.removeItem(THEME_KEY);
    } else {
      localStorage.setItem(THEME_KEY, theme);
    }
  } catch {
    // The attribute is already set; it simply will not survive a reload.
  }

  /*
   * The address-bar colour on mobile, kept in step.
   *
   * It is a meta tag with a literal in it, which the stylesheet cannot reach.
   * Left alone, a light interface keeps a near-black browser chrome above it.
   */
  const meta = document.querySelector('meta[name="theme-color"]');

  if (meta) {
    meta.setAttribute(
      'content',
      getComputedStyle(root).getPropertyValue('--color-app').trim() || '#08080F',
    );
  }

  /*
   * And the desktop shell's caption buttons, which are the system's and not
   * ours - the one part of the window this stylesheet cannot reach.
   */
  tellShellTheme(resolvedTheme());
}

/**
 * Follow the device while the choice is `system`.
 *
 * Without this, somebody on `system` whose laptop switches at sunset keeps the
 * caption buttons from the old theme - the page repaints itself through the
 * media query, and the part of the window the page does not own does not.
 *
 * Returns a teardown, though in practice this lives for the life of the app.
 */
export function watchSystemTheme(): () => void {
  const query = window.matchMedia?.('(prefers-color-scheme: light)');

  if (!query) return () => {};

  const onChange = () => {
    if (getTheme() === 'system') setTheme('system');
  };

  query.addEventListener('change', onChange);

  return () => query.removeEventListener('change', onChange);
}

/** What is actually on screen, once `system` has been resolved. */
export function resolvedTheme(): 'light' | 'dark' {
  const chosen = getTheme();

  if (chosen !== 'system') return chosen;

  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}
